import React, { useState, useEffect } from 'react';
import {
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  UserIcon,
  PhoneIcon,
  PaperAirplaneIcon,
  InformationCircleIcon,
  PhotoIcon,
  DocumentTextIcon,
  QueueListIcon
} from '@heroicons/react/24/outline';
import { api, API_BASE_URL, getAuthToken } from '../services/api';
import toast from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';
import ImageUploadPreview from '../components/ImageUploadPreview';
import BulkMessageFileUpload from '../components/BulkMessageFileUpload';

const SendTextPage = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [activeTab, setActiveTab] = useState('single'); // 'single' or 'bulk'
  
  // Single message state
  const [message, setMessage] = useState('');
  const [chatId, setChatId] = useState('');
  const [imageData, setImageData] = useState(null);
  
  // Bulk message state
  const [campaignName, setCampaignName] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkImageData, setBulkImageData] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getWhatsAppClients();
      setAgents(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching WhatsApp clients:', error);
      toast.error('Failed to load WhatsApp clients');
    } finally {
      setLoading(false);
    }
  };

  // Prepare WhatsApp clients data for SearchableSelect
  const agentOptions = agents && Object.keys(agents).length > 0
    ? Object.values(agents)
        .filter(client => client.status === 'active')
        .map((client) => ({
          value: client.id,
          label: client.agent_name || client.display_name || `Client ${client.client_id}`,
          description: client.whatsapp_number || client.client_id,
          icon: '📱',
          port: client.port,
          webhookApiKey: client.webhook_api_key || client.api_key,
          clientId: client.client_id
        }))
    : [];

  const handleSendMessage = async () => {
    if (!selectedAgent || !chatId || (!message && !imageData)) {
      toast.error('Please fill in either message text or select an image');
      return;
    }

    setSending(true);
    try {
      const client = Object.values(agents).find(a => a.id === selectedAgent);
      if (!client) {
        toast.error('WhatsApp client not found');
        return;
      }

      let response;
      
      if (imageData) {
        // Upload image first, then send message
        let imageUrl;
        
        if (imageData.url) {
          // Already uploaded URL (from bulk campaign)
          imageUrl = imageData.url;
        } else if (imageData.actualFile) {
          // Upload the actual file now
          const formData = new FormData();
          formData.append('file', imageData.actualFile);

          const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/upload/public-image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${getAuthToken()}`
            },
            body: formData
          });

          if (!uploadResponse.ok) {
            const error = await uploadResponse.json();
            throw new Error(error.error || 'Failed to upload image');
          }

          const uploadResult = await uploadResponse.json();
          imageUrl = uploadResult.url;
        } else {
          throw new Error('No image file available');
        }

        // Send image message using the uploaded URL
        response = await api.sendImageMessage(
          client.client_id,
          chatId,
          imageUrl,
          message || '' // caption
        );
      } else {
        // Send text message
        response = await api.sendTextMessage(
          client.client_id,
          chatId,
          message
        );
      }

      toast.success('Message sent successfully!');
      setMessage('');
      setChatId('');
      setImageData(null);
    } catch (error) {
      console.error('Error sending message:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(error.message || 'Failed to send message');
      }
    } finally {
      setSending(false);
    }
  };

  const handleImageRemove = () => {
    setImageData(null);
  };

  const handleBulkFileChange = (fileData) => {
    setBulkFile(fileData);
  };

  const handleBulkFileRemove = () => {
    setBulkFile(null);
    setBulkImageData(null);
  };

  const handleBulkImageRemove = () => {
    setBulkImageData(null);
  };

  const handleSendBulkMessage = async () => {
    if (!selectedAgent || !bulkFile) {
      toast.error('Please select a WhatsApp agent and upload a recipient file');
      return;
    }

    if (!campaignName) {
      toast.error('Please enter a campaign name');
      return;
    }

    if (!bulkMessage && !bulkImageData) {
      toast.error('Please provide a campaign message or upload an image');
      return;
    }
    
    // If no image, message must be provided
    if (!bulkImageData && !bulkMessage.trim()) {
      toast.error('Campaign message is required when no image is provided');
      return;
    }

    setUploadingBulk(true);
    try {
      const client = Object.values(agents).find(a => a.id === selectedAgent);
      if (!client) {
        toast.error('WhatsApp client not found');
        return;
      }

      let imageUrl = null;
      
      // Upload image if we have one
      if (bulkImageData) {
        if (bulkImageData.url) {
          // Already uploaded URL
          imageUrl = bulkImageData.url;
        } else if (bulkImageData.actualFile) {
          // Upload the actual file now
          const formData = new FormData();
          formData.append('file', bulkImageData.actualFile);

          const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/upload/public-image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${getAuthToken()}`
            },
            body: formData
          });

          if (!uploadResponse.ok) {
            const error = await uploadResponse.json();
            throw new Error(error.error || 'Failed to upload image');
          }

          const uploadResult = await uploadResponse.json();
          imageUrl = uploadResult.url;
        } else {
          throw new Error('No image file available');
        }
      }

      const campaignData = {
        client_id: client.client_id,
        campaign_name: campaignName || `Bulk Campaign - ${new Date().toLocaleString()}`,
        message_type: imageUrl ? 'image' : 'text',
        text_content: bulkMessage || '',
        image_data: imageUrl,
        file_path: bulkFile.path,
        original_filename: bulkFile.name,
        file_type: bulkFile.name.split('.').pop().toUpperCase() || 'CSV',
        file_size: bulkFile.size
      };

      const response = await api.sendBulkMessage(campaignData);

      toast.success('Bulk message campaign created successfully! It will be processed in the background.');
      
      // Reset bulk form
      setCampaignName('');
      setBulkMessage('');
      setBulkFile(null);
      setBulkImageData(null);
      
    } catch (error) {
      console.error('Error sending bulk message:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(error.message || 'Failed to create bulk message campaign');
      }
    } finally {
      setUploadingBulk(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ChatBubbleLeftRightIcon className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Send WhatsApp Messages</h1>
                  <p className="text-sm text-gray-600">Send single messages or bulk campaigns to WhatsApp contacts</p>
                </div>
              </div>
              <button
                onClick={fetchAgents}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('single')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'single'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <PaperAirplaneIcon className="h-5 w-5" />
                  <span>Single Message</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('bulk')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'bulk'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <QueueListIcon className="h-5 w-5" />
                  <span>Bulk Message</span>
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              {activeTab === 'single' ? (
                <>
                  <PaperAirplaneIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <h2 className="text-lg font-medium text-gray-900">Single Message Details</h2>
                </>
              ) : (
                <>
                  <QueueListIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <h2 className="text-lg font-medium text-gray-900">Bulk Message Campaign Details</h2>
                </>
              )}
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); activeTab === 'single' ? handleSendMessage() : handleSendBulkMessage(); }} className="px-6 py-6 space-y-6">

            {/* Agent Selection */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Select WhatsApp Agent
              </h3>

              <SearchableSelect
                label="WhatsApp Agent"
                name="client"
                options={agentOptions}
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                placeholder="Search and select a WhatsApp agent..."
                isRequired={true}
                isSearchable={true}
                isClearable={true}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">WhatsApp Agent Selection</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Choose a connected WhatsApp agent to send your message. Only agents with active connections are available.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {activeTab === 'single' ? (
              <>
                {/* Single Message Details */}
                <div className="space-y-6">
                  <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Message Information
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Chat ID <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={chatId}
                        onChange={(e) => setChatId(e.target.value)}
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                        placeholder="233554694531"
                        required
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Enter phone number in international format (without +)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message Content <span className="text-xs text-gray-500">(Required if no image)</span>
                    </label>
                    <div className="relative">
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 resize-none"
                        placeholder="Type your message here (this will be the caption if you upload an image)..."
                      />
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-sm text-gray-500">
                        {!imageData ? 'Enter the text message you want to send' : 'This text will be used as the image caption'}
                      </p>
                      <span className="text-sm text-gray-500">
                        {message.length} characters
                      </span>
                    </div>
                  </div>

                  <ImageUploadPreview
                    image={imageData}
                    onChange={setImageData}
                    onRemove={handleImageRemove}
                  />
                </div>
              </>
            ) : (
              <>
                {/* Bulk Message Details */}
                <div className="space-y-6">
                  <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Campaign Information
                  </h3>

                  <BulkMessageFileUpload
                    onFileChange={handleBulkFileChange}
                    uploadedFile={bulkFile}
                    onRemove={handleBulkFileRemove}
                    uploading={uploadingBulk}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Campaign Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <DocumentTextIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                        placeholder="Enter a name for this campaign (e.g., March Promotion)"
                        required
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Give your campaign a memorable name for tracking purposes
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Campaign Message <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <ChatBubbleLeftRightIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <textarea
                        value={bulkMessage}
                        onChange={(e) => setBulkMessage(e.target.value)}
                        rows={4}
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 resize-none"
                        placeholder="Enter your bulk message here (this will be sent to all recipients in your file)..."
                        required
                      />
                    </div>

                    <div className="flex justify-between items-center mt-2">
                      <p className="text-sm text-gray-500">
                        {!bulkImageData ? 'This message will be sent to all recipients' : 'This text will be used as the image caption for all recipients'}
                      </p>
                      <span className="text-sm text-gray-500">
                        {bulkMessage.length} characters
                      </span>
                    </div>
                  </div>

                  <ImageUploadPreview
                    image={bulkImageData}
                    onChange={setBulkImageData}
                    onRemove={handleBulkImageRemove}
                  />

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex">
                      <DocumentTextIcon className="h-5 w-5 text-amber-400" />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-amber-800">Bulk Message Campaign</h4>
                        <p className="mt-1 text-sm text-amber-700">
                          <ul className="list-disc list-inside space-y-1">
                            <li>Messages will be processed in the background</li>
                            <li>Large files are split into batches for better performance</li>
                            <li>You can track campaign status in the dashboard</li>
                            <li>Failed recipients can be retried automatically</li>
                          </ul>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setSelectedAgent('');
                  if (activeTab === 'single') {
                    setMessage('');
                    setChatId('');
                    setImageData(null);
                  } else {
                    setCampaignName('');
                    setBulkMessage('');
                    setBulkFile(null);
                    setBulkImageData(null);
                  }
                }}
                disabled={sending || uploadingBulk}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Form
              </button>

              <button
                type="submit"
                disabled={
                  !selectedAgent || 
                  (activeTab === 'single' && (!chatId || (!message && !imageData))) ||
                  (activeTab === 'bulk' && (!bulkFile || (!bulkMessage && !bulkImageData))) ||
                  sending || uploadingBulk
                }
                className="px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-lg hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {(sending || uploadingBulk) ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    {activeTab === 'single' ? 'Sending Message...' : 'Creating Campaign...'}
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    {activeTab === 'single' 
                      ? `Send ${imageData ? 'Image' : 'Message'}` 
                      : 'Create Bulk Campaign'
                    }
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SendTextPage;